/**
 * ProfileLocationAlerts - Compact geofence alert settings card.
 * Matches the push / digest toggle cards in ProfileSection.
 * @atomic @idempotent
 */
import { memo, useState, useCallback, useEffect } from 'react'
import { MapPinIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { useDistanceUnit } from '../../../hooks/useDistanceUnit'
import { useAuth } from '../../../context/AuthContext'
import { authAPI } from '../../../services/api'
import { useAsyncOperation } from '../../../hooks/useAsyncOperation'
import { createLogger } from '../../../utils/logger'
import { toast } from '../../../services/toastService'

const logger = createLogger('ProfileLocationAlerts')

// Available radius options in km
const RADIUS_OPTIONS_KM = [1, 2, 5, 10, 25]

const ProfileLocationAlerts = memo(function ProfileLocationAlerts({ user }) {
  const { t } = useTranslation()
  const { convertFromKm, unit } = useDistanceUnit()
  const { updateUserState } = useAuth()
  const { execute, loading } = useAsyncOperation()

  const [lat, setLat] = useState(user?.homeLatitude ?? null)
  const [lon, setLon] = useState(user?.homeLongitude ?? null)
  const [radiusKm, setRadiusKm] = useState(user?.geofenceRadiusKm ?? 5)
  const [detecting, setDetecting] = useState(false)

  // Sync when AuthContext user updates (e.g. after initial profile fetch completes)
  useEffect(() => {
    setLat(user?.homeLatitude ?? null)
    setLon(user?.homeLongitude ?? null)
    setRadiusKm(user?.geofenceRadiusKm ?? 5)
  }, [user?.homeLatitude, user?.homeLongitude, user?.geofenceRadiusKm])

  const handleDetectLocation = useCallback((e) => {
    e.preventDefault?.()
    if (!navigator.geolocation) {
      toast.error(t('profile.locationAlerts.geolocationError', 'Geolocation not supported'))
      return
    }
    setDetecting(true)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const newLat = pos.coords.latitude
        const newLon = pos.coords.longitude
        setLat(newLat)
        setLon(newLon)
        setDetecting(false)
        logger.info('Location detected — auto-saving', { lat: newLat, lon: newLon })
        // Auto-save immediately with fresh coordinates (don't rely on state which may not have updated yet)
        await execute(
          () => authAPI.updateProfile({ homeLatitude: newLat, homeLongitude: newLon, geofenceRadiusKm: radiusKm }),
          {
            onSuccess: () => {
              updateUserState({ homeLatitude: newLat, homeLongitude: newLon, geofenceRadiusKm: radiusKm })
              toast.success(t('profile.locationAlerts.saved', 'Location alerts saved!'))
            },
            onError: (err) => {
              logger.error('Failed to auto-save location', err)
              toast.error(t('common.errorOccurred', 'Something went wrong'))
            }
          }
        )
      },
      (err) => {
        setDetecting(false)
        logger.warn('Geolocation failed', err)
        toast.error(t('profile.locationAlerts.geolocationError', 'Could not detect location'))
      },
      { timeout: 10000, maximumAge: 60000 }
    )
  }, [t, execute, radiusKm, updateUserState])

  const handleSave = useCallback(async (e) => {
    e.preventDefault?.()
    // Always save radius; only include lat/lon if a location is set
    const payload = { geofenceRadiusKm: radiusKm }
    if (lat != null && lon != null) {
      payload.homeLatitude = lat
      payload.homeLongitude = lon
    }
    await execute(
      () => authAPI.updateProfile(payload),
      {
        onSuccess: () => {
          updateUserState({ geofenceRadiusKm: radiusKm, ...(lat != null ? { homeLatitude: lat, homeLongitude: lon } : {}) })
          toast.success(t('profile.locationAlerts.saved', 'Location alerts saved!'))
        },
        onError: (err) => {
          logger.error('Failed to save location alerts', err)
          toast.error(t('common.errorOccurred', 'Something went wrong'))
        }
      }
    )
  }, [lat, lon, radiusKm, execute, updateUserState, t])

  const handleClear = useCallback(async (e) => {
    e.preventDefault?.()
    await execute(
      () => authAPI.updateProfile({ homeLatitude: null, homeLongitude: null }),
      {
        onSuccess: () => {
          setLat(null)
          setLon(null)
          updateUserState({ homeLatitude: null, homeLongitude: null })
          toast.success(t('profile.locationAlerts.cleared', 'Location alerts cleared'))
        },
        onError: (err) => {
          logger.error('Failed to clear location', err)
          toast.error(t('common.errorOccurred', 'Something went wrong'))
        }
      }
    )
  }, [execute, updateUserState, t])

  const hasLocation = lat != null && lon != null

  return (
    <div className="bg-white rounded-xl border border-gray-200/70 shadow-sm px-4 py-3">
      {/* Header row — icon + title + radius selector inline */}
      <div className="flex items-center gap-2">
        <MapPinIcon className="h-5 w-5 text-primary flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">
            {t('profile.locationAlerts.title', 'Nearby Alerts')}
          </p>
          <p className="text-xs text-gray-500 leading-snug">
            {t('profile.locationAlerts.description', 'Get notified when vendors open near your saved location')}
          </p>
        </div>
        {/* Radius selector — right-aligned in header */}
        <select
          value={radiusKm}
          onChange={(e) => setRadiusKm(Number(e.target.value))}
          className="text-xs border border-gray-200 rounded-lg pl-2 pr-6 py-1 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary flex-shrink-0"
          title={t('profile.locationAlerts.radius', 'Alert radius')}
        >
          {RADIUS_OPTIONS_KM.map((km) => (
            <option key={km} value={km}>
              {convertFromKm(km, 0)} {unit}
            </option>
          ))}
        </select>
      </div>

      {/* Location status + action buttons */}
      <div className="mt-2.5 flex items-center gap-2">
        {hasLocation ? (
          <span className="flex-1 text-xs text-green-600 font-medium truncate">
            ✓ {lat.toFixed(4)}, {lon.toFixed(4)}
          </span>
        ) : (
          <span className="flex-1 text-xs text-gray-400">
            {t('profile.locationAlerts.noLocation', 'No location set yet')}
          </span>
        )}

        {hasLocation && (
          <button
            type="button"
            disabled={loading}
            onClick={handleClear}
            onTouchEnd={handleClear}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
            title={t('app.clear', 'Clear')}
          >
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        )}

        <button
          type="button"
          disabled={detecting || loading}
          onClick={handleDetectLocation}
          onTouchEnd={handleDetectLocation}
          className="py-1 px-2.5 rounded-lg text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors disabled:opacity-50 flex-shrink-0"
        >
          {detecting
            ? t('app.loading', 'Detecting…')
            : hasLocation
            ? t('profile.locationAlerts.updateLocation', 'Update')
            : t('profile.locationAlerts.setLocation', 'Use location')}
        </button>

        <button
          type="button"
          disabled={loading}
          onClick={handleSave}
          onTouchEnd={handleSave}
          className="py-1 px-2.5 rounded-lg text-xs font-medium bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
        >
          {loading ? t('app.loading', 'Saving…') : t('app.save', 'Save')}
        </button>
      </div>
    </div>
  )
})

export default ProfileLocationAlerts
